'use client';

import React from 'react';
import styles from './scroll-view.module.css';
import { cn } from '../../utils/cn';
import type { ScrollViewProps } from './scroll-view.types';
import {
    DEFAULT_AXIS,
    DEFAULT_THICKNESS,
    DEFAULT_TRACK_VISIBILITY_RATIO,
    DEFAULT_MIN_THUMB_SIZE,
} from './scroll-view.constants';
import { useScrollView } from '../../utils/use-scroll-view';

export const ScrollView: React.FC<ScrollViewProps> = ({
    children,
    isVisible = true,
    axis = DEFAULT_AXIS,
    size,
    className,
    thickness = DEFAULT_THICKNESS,
    trackLengthPercent = DEFAULT_TRACK_VISIBILITY_RATIO,
    classNames,
    minThumbSize = DEFAULT_MIN_THUMB_SIZE,
}) => {
    const {
        contentRef,
        trackXRef,
        trackYRef,
        wrapperStyle,
        contentStyle,
        thumbX,
        thumbY,
        hasX,
        hasY,
        onThumbDownX,
        onThumbDownY,
        onPointerMove,
        onPointerUp,
        onTrackClickX,
        onTrackClickY,
        onContentPointerDown,
        onContentPointerMove,
        onContentPointerUp,
        onKeyDown,
    } = useScrollView({ axis, minThumbSize, size });

    const { trackX, trackY, thumbX: thumbXClass, thumbY: thumbYClass, content } = classNames ?? {};

    return (
        <div className={cn(styles.wrapper, className)} style={wrapperStyle}>
            <div
                ref={contentRef}
                tabIndex={0}
                onKeyDown={onKeyDown}
                className={cn(styles.content, content)}
                onPointerDown={onContentPointerDown}
                onPointerMove={onContentPointerMove}
                onPointerUp={onContentPointerUp}
                onPointerLeave={onContentPointerUp}
                style={contentStyle}
            >
                {children}
            </div>

            {isVisible && (
                <div className={styles.trackWrap}>
                    {hasX && (
                        <div
                            ref={trackXRef}
                            data-testid="track-x"
                            className={cn(styles.track, styles.trackX, trackX)}
                            style={{ width: `${trackLengthPercent * 100}%`, height: thickness }}
                            onMouseDown={onTrackClickX}
                        >
                            {thumbX.visible && (
                                <div
                                    className={cn(styles.thumb, thumbXClass)}
                                    data-testid="thumb-x"
                                    style={{
                                        width: thumbX.size,
                                        height: thickness,
                                        transform: `translateX(${thumbX.pos}px)`,
                                    }}
                                    onPointerDown={onThumbDownX}
                                    onPointerMove={onPointerMove}
                                    onPointerUp={onPointerUp}
                                />
                            )}
                        </div>
                    )}

                    {hasY && (
                        <div
                            ref={trackYRef}
                            data-testid="track-y"
                            className={cn(styles.track, styles.trackY, trackY)}
                            style={{ height: `${trackLengthPercent * 100}%`, width: thickness }}
                            onMouseDown={onTrackClickY}
                        >
                            {thumbY.visible && (
                                <div
                                    className={cn(styles.thumb, thumbYClass)}
                                    data-testid="thumb-y"
                                    style={{
                                        height: thumbY.size,
                                        width: thickness,
                                        transform: `translateY(${thumbY.pos}px)`,
                                    }}
                                    onPointerDown={onThumbDownY}
                                    onPointerMove={onPointerMove}
                                    onPointerUp={onPointerUp}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
